import { useOktaAuth } from '@okta/okta-react';
import { createContext, useContext, useEffect, useState } from 'react';
import { UnitSelectionContext } from './UnitSelectionContext';

export const LoggedInContext = createContext({});

// Reads RFC 7807 ProblemDetail from the response body without consuming the caller's stream.
export async function parseProblem(response) {
    const fallback = {
        title: 'Something went wrong',
        detail: 'We are experiencing technical issues. Please try again later.',
        traceId: response.headers.get('X-Request-Id') || null,
        status: response.status,
    };
    try {
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('json')) return fallback;
        const body = await response.clone().json();
        return {
            title: body.title || fallback.title,
            detail: body.detail || fallback.detail,
            traceId: body.traceId || fallback.traceId,
            status: body.status || response.status,
        };
    } catch {
        return fallback;
    }
}

export const LoggedInContextProvider = ({
    children,
    impersonatedId,
    impersonatedIsAdmin,
}) => {
    const { authState } = useOktaAuth();
    const { selectedUnit } = useContext(UnitSelectionContext);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authState) setLoading(false);
    }, [authState, impersonatedId, impersonatedIsAdmin]);

    const makeRequest = async (url, requestOptions) => {
        const defaultHeaders = {
            'content-type': 'application/json',
            Authorization: `Bearer ${authState.accessToken.accessToken}`,
            actingAs: impersonatedId,
        };

        // Admin-only routing hint; backend still authorizes against the token.
        if (selectedUnit) {
            defaultHeaders['X-Job-Bid-Unit'] = selectedUnit;
        }

        const headers = {
            ...defaultHeaders,
            ...requestOptions?.headers,
        };

        // Remove content-type if explicitly set to null (for FormData)
        if (requestOptions?.headers?.['content-type'] === null) {
            delete headers['content-type'];
        }

        const response = await fetch(url, {
            ...requestOptions,
            headers,
        });

        if (!response.ok) {
            response.problem = await parseProblem(response);
        }
        return response;
    };

    const loggedInContextValue = {
        authState,
        impersonatedId,
        impersonatedIsAdmin,
        loading,
        makeRequest,
    };

    return (
        <LoggedInContext.Provider value={loggedInContextValue}>
            {children}
        </LoggedInContext.Provider>
    );
};
